/**
 * PERFOLAB — Capa centralizada de monitoreo (Sentry)
 *
 * Reglas:
 * - Solo se inicializa en producción real (no localhost, no preview).
 * - No bloquea UI ni el flujo offline.
 * - No registra datos sensibles (emails completos, tokens, contraseñas).
 * - Todos los eventos llevan contexto operacional estructurado.
 * - NO usar Sentry.captureException directamente en la app: usar los helpers
 *   captureOperationalError / captureSyncError / captureFuelError / etc.
 */

import * as Sentry from "@sentry/react";

import {
  APP_VERSION,
  BUILD_VERSION,
  RELEASE_CHANNEL,
  COMMIT_HASH,
} from "@/config/version";

const DSN =
  "https://7b0941fb99584fc0586d8e56dc61518a@o4511466035412992.ingest.us.sentry.io/4511469442236416";

type Environment = "production" | "staging" | "preview" | "development";
type DeviceType = "mobile" | "tablet" | "desktop";

let _initialized = false;
let _enabled = false;

function detectEnvironment(): Environment {
  if (typeof window === "undefined") return "development";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host.includes("id-preview--") || host.includes("lovableproject.com"))
    return "preview";
  if (host === "perfolab.lovable.app" || host.includes("staging"))
    return "staging";
  return "production";
}

function detectDevice(): DeviceType {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  const ua = navigator.userAgent || "";
  const isMobileUA = /Mobi|Android|iPhone/i.test(ua);
  const isTabletUA = /iPad|Tablet/i.test(ua);
  if (isTabletUA || (w >= 600 && w < 1100 && isMobileUA)) return "tablet";
  if (isMobileUA || w < 600) return "mobile";
  return "desktop";
}

/**
 * Inicializa Sentry. Idempotente. Solo activa en producción.
 */
export function initSentry(): void {
  if (_initialized) return;
  _initialized = true;

  const env = detectEnvironment();
  if (env !== "production" && env !== "staging") {
    // Dev/preview: no enviar nada a Sentry
    return;
  }

  try {
    Sentry.init({
      dsn: DSN,
      environment: env,
      release: `perfolab@${APP_VERSION}`,
      // Performance ligero: solo navegación
      tracesSampleRate: 0.05,
      // Sin session replay por ahora
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Sin PII automática
      sendDefaultPii: false,
      // Filtro de eventos: no enviar errores de red offline esperados
      beforeSend(event, hint) {
        try {
          const err = hint?.originalException as Error | undefined;
          const msg = (err?.message || event.message || "").toLowerCase();

          // Ignorar errores típicos de modo offline esperado
          if (
            msg.includes("failed to fetch") &&
            typeof navigator !== "undefined" &&
            !navigator.onLine
          ) {
            return null;
          }
          if (msg.includes("networkerror") && !navigator.onLine) return null;
          if (msg.includes("load failed") && !navigator.onLine) return null;

          // Scrubbing defensivo de PII en mensajes
          if (event.message) {
            event.message = scrubPII(event.message);
          }
        } catch {
          // never break sending
        }
        return event;
      },
      // No capturar console.log como breadcrumb (ruido offline)
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
    });

    Sentry.setTag("platform", detectDevice());
    Sentry.setTag("app", "perfolab");
    Sentry.setTag("app_version", APP_VERSION);
    Sentry.setTag("build_version", BUILD_VERSION);
    Sentry.setTag("release_channel", RELEASE_CHANNEL);
    Sentry.setTag("commit", COMMIT_HASH);
    Sentry.setContext("app", {
      version: APP_VERSION,
      build: BUILD_VERSION,
      commit: COMMIT_HASH,
      environment: env,
      channel: RELEASE_CHANNEL,
      device: detectDevice(),
    });

    _enabled = true;
  } catch (e) {
    // Nunca romper la app por Sentry
    console.warn("[sentry] init failed:", e);
  }
}

function scrubPII(input: string): string {
  return input
    // emails -> dominio
    .replace(/[a-z0-9._%+-]+@([a-z0-9.-]+\.[a-z]{2,})/gi, "***@$1")
    // tokens largos
    .replace(/\b[A-Za-z0-9_-]{32,}\b/g, "***");
}

/**
 * Actualiza el usuario activo (id+rol). No envía email ni nombre.
 */
export function setMonitoringUser(params: {
  userId?: string | null;
  role?: string | null;
  project?: string | null;
  machineNumber?: string | null;
}): void {
  if (!_enabled) return;
  try {
    Sentry.setUser(
      params.userId
        ? { id: params.userId }
        : null
    );
    Sentry.setTag("role", params.role ?? "unknown");
    Sentry.setTag("project", params.project ?? "unknown");
    Sentry.setTag("machine", params.machineNumber ?? "unknown");
  } catch {/* noop */}
}

export interface OperationalContext {
  userId?: string | null;
  role?: string | null;
  project?: string | null;
  machineNumber?: string | null;
  drillHole?: string | null;
  reportId?: string | null;
  extra?: Record<string, unknown>;
}

export type MonitoringModule =
  | "sync"
  | "reports"
  | "tools"
  | "fuel"
  | "auth"
  | "pdf"
  | "directional"
  | "general";

type Priority = "low" | "normal" | "high";

function buildScope(
  module: MonitoringModule,
  priority: Priority,
  ctx?: OperationalContext
): (scope: Sentry.Scope) => void {
  return (scope) => {
    scope.setTag("module", module);
    scope.setTag("priority", priority);
    scope.setTag(
      "online",
      typeof navigator !== "undefined" ? String(navigator.onLine) : "unknown"
    );
    scope.setTag("device", detectDevice());
    if (ctx?.role) scope.setTag("role", ctx.role);
    if (ctx?.project) scope.setTag("project", ctx.project);
    if (ctx?.machineNumber) scope.setTag("machine", ctx.machineNumber);
    if (ctx?.drillHole) scope.setTag("drill_hole", ctx.drillHole);
    scope.setContext("operational", {
      userId: ctx?.userId ?? null,
      role: ctx?.role ?? null,
      project: ctx?.project ?? null,
      machineNumber: ctx?.machineNumber ?? null,
      drillHole: ctx?.drillHole ?? null,
      reportId: ctx?.reportId ?? null,
      appVersion: APP_VERSION,
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
      timestamp: new Date().toISOString(),
      deviceType: detectDevice(),
      ...(ctx?.extra ?? {}),
    });
  };
}

// Debounce por hash de mensaje+module para evitar floods
const _recent = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;
function shouldSend(module: string, key: string): boolean {
  const k = `${module}:${key}`;
  const now = Date.now();
  const last = _recent.get(k) ?? 0;
  if (now - last < DEDUP_WINDOW_MS) return false;
  _recent.set(k, now);
  // garbage collect ocasional
  if (_recent.size > 200) {
    for (const [kk, t] of _recent) {
      if (now - t > DEDUP_WINDOW_MS) _recent.delete(kk);
    }
  }
  return true;
}

function send(
  module: MonitoringModule,
  priority: Priority,
  err: unknown,
  message: string,
  ctx?: OperationalContext
): string | null {
  if (!_enabled) return null;
  try {
    const dedupKey = `${message}|${ctx?.reportId ?? ""}|${ctx?.drillHole ?? ""}`;
    if (!shouldSend(module, dedupKey)) return null;

    let eventId: string | null = null;
    Sentry.withScope((scope) => {
      buildScope(module, priority, ctx)(scope);
      scope.setLevel(priority === "high" ? "error" : "warning");
      if (err instanceof Error) {
        eventId = Sentry.captureException(err);
      } else {
        eventId = Sentry.captureMessage(scrubPII(message));
      }
    });
    return eventId;
  } catch {
    return null;
  }
}

// ====== Helpers públicos ======

export function captureOperationalError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("general", "normal", err, message, ctx);
}

export function captureSyncError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("sync", "high", err, message, ctx);
}

export function captureReportError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("reports", "high", err, message, ctx);
}

export function capturePdfError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("pdf", "normal", err, message, ctx);
}

export function captureToolError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("tools", "high", err, message, ctx);
}

export function captureFuelError(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("fuel", "high", err, message, ctx);
}

export function captureAuthEvent(
  message: string,
  err?: unknown,
  ctx?: OperationalContext
): string | null {
  return send("auth", "high", err, message, ctx);
}

/** Breadcrumb ligero para trazas operacionales. */
export function trackOperationalBreadcrumb(
  category: MonitoringModule,
  message: string,
  data?: Record<string, unknown>
): void {
  if (!_enabled) return;
  try {
    Sentry.addBreadcrumb({
      category,
      message: scrubPII(message),
      level: "info",
      data,
    });
  } catch {/* noop */}
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const isSentryEnabled = () => _enabled;
